import React, { useRef, useState, useMemo } from 'react';
import type { FC } from 'react';
import { Box, IconButton, Popover, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import { useTheme } from '@mui/material/styles';
import useSettings from '../../hooks/useSettings';
import { THEMES } from '../../constants';

export interface ThemeToggleProps {
  onThemeSelected?: (theme: string) => void;
}

export const ThemeToggle: FC<ThemeToggleProps> = ({ onThemeSelected }) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const { settings, saveSettings } = useSettings();

  const selectedTheme = useMemo(() => {
    return settings.theme ?? THEMES.LIGHT;
  }, [settings.theme]);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleThemeChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextTheme: string | null
  ): void => {
    if (!nextTheme) {
      return;
    }

    saveSettings({
      ...settings,
      theme: nextTheme
    });

    onThemeSelected?.(nextTheme);
  };

  return (
      <>
        <Tooltip
            title="Color scheme"
        >
          <IconButton
              size="medium"
              color="inherit"
              onClick={handleOpen}
              ref={anchorRef}
              sx={{
                color: selectedTheme === THEMES.DARK ? theme.palette.common.white : theme.palette.common.black,
                ml: 1
              }}
          >
            {selectedTheme === THEMES.DARK ? (
                <DarkModeIcon
                    fontSize="small"
                />
            ) : (
                <LightModeIcon
                    fontSize="small"
                />
            )}
          </IconButton>
        </Tooltip>

        <Popover
            anchorEl={anchorRef.current}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom'
            }}
            transformOrigin={{
              horizontal: 'right',
              vertical: 'top'
            }}
            keepMounted
            onClose={handleClose}
            open={open}
            slotProps={{
              paper: {
                sx: {
                  mt: 1.5,
                  p: 2,
                  boxShadow: theme.shadows[10],
                  borderRadius: 2
                }
              }
            }}
        >
          <Typography
              color="textPrimary"
              variant="subtitle2"
              sx={{ fontWeight: 600 }}
          >
            Color scheme
          </Typography>
          <ToggleButtonGroup
              exclusive
              size="small"
              value={selectedTheme}
              sx={{
                mt: 1.5,
                p: 0.5,
                backgroundColor: 'background.default',
                borderRadius: 999,
                '& .MuiToggleButton-root': {
                  border: '0 !important',
                  borderRadius: '999px !important',
                  textTransform: 'none',
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  justifyContent: 'center',
                  color: 'text.secondary'
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  backgroundColor: 'background.paper',
                  border: `2px solid ${theme.palette.primary.main} !important`,
                  color: 'text.primary',
                  boxShadow: theme.shadows[1]
                }
              }}
              onChange={handleThemeChange}
          >
            <ToggleButton value={THEMES.LIGHT}>
              <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
              >
                <LightModeIcon fontSize="small" />
                Light
              </Box>
            </ToggleButton>
            <ToggleButton value={THEMES.DARK}>
              <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
              >
                <DarkModeIcon fontSize="small" />
                Dark
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Popover>
      </>
  );
};

export default ThemeToggle;